import React, { useState, useEffect } from 'react';

function parseLevels(text) {
  return text
    .split(/[,\s]+/)
    .map((s) => parseInt(s.trim(), 10))
    .filter((n) => Number.isFinite(n) && n > 0);
}

function formatLevels(arr) {
  return Array.isArray(arr) ? arr.join(', ') : '';
}

function StateBanner({ config, killTriggered, fallbackPolling }) {
  if (!config) return null;
  if (killTriggered) {
    return (
      <div className="ab-banner ab-banner-danger">
        <strong>Kill-switch acionado.</strong> Auto-block desativado por proteção
        ({killTriggered.reason}). IPs já banidos permanecem até expirar; novos não serão bloqueados.
      </div>
    );
  }
  if (config.enabled && config.dry_run) {
    return (
      <div className="ab-banner ab-banner-warn">
        <strong>Modo observação ativo.</strong> Bans são registrados no histórico mas NÃO
        bloqueiam no FIREWALL. Use para calibrar threshold antes de promover para bloqueio efetivo.
      </div>
    );
  }
  if (config.enabled && !config.dry_run) {
    return (
      <div className="ab-banner ab-banner-success">
        <strong>Auto-block ativo.</strong> Bloqueios progressivos
        ({(config.progressive_levels_seconds || []).map((s) => `${Math.floor(s / 60)}min`).join(' → ')})
        sendo aplicados no FIREWALL.
      </div>
    );
  }
  if (!config.enabled) {
    return (
      <div className="ab-banner ab-banner-info">
        <strong>Auto-block desativado.</strong> Nenhuma detecção ou bloqueio ocorrendo.
      </div>
    );
  }
  if (fallbackPolling) {
    return (
      <div className="ab-banner ab-banner-info">
        Atualizando via polling — conexão em tempo real indisponível.
      </div>
    );
  }
  return null;
}

function AutoBlockConfigCard({ config, killSwitch, onSave, onKillSwitch, busy }) {
  const [form, setForm] = useState(null);
  const [levelsText, setLevelsText] = useState('');
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!config) return;
    setForm({
      enabled: config.enabled,
      dry_run: config.dry_run,
      maxretry: config.maxretry,
      findtime_seconds: config.findtime_seconds,
      progressive_enabled: config.progressive_enabled,
      progressive_base_seconds: config.progressive_base_seconds,
      progressive_max_seconds: config.progressive_max_seconds,
      auto_kill_threshold: config.auto_kill_threshold,
      auto_kill_window_min: config.auto_kill_window_min,
    });
    setLevelsText(formatLevels(config.progressive_levels_seconds));
    setError(null);
  }, [config?.version]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!form) return null;

  const setField = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const handleSave = () => {
    const levels = parseLevels(levelsText);
    if (levels.length === 0) {
      setError('Níveis progressivos não pode ser vazio');
      return;
    }
    setError(null);
    const payload = {
      enabled: !!form.enabled,
      dry_run: !!form.dry_run,
      maxretry: parseInt(form.maxretry, 10),
      findtime_seconds: parseInt(form.findtime_seconds, 10),
      progressive_enabled: !!form.progressive_enabled,
      progressive_levels_seconds: levels,
      progressive_base_seconds: parseInt(form.progressive_base_seconds, 10),
      progressive_max_seconds: parseInt(form.progressive_max_seconds, 10),
      auto_kill_threshold: parseInt(form.auto_kill_threshold, 10),
      auto_kill_window_min: parseInt(form.auto_kill_window_min, 10),
    };
    onSave(payload, config.version);
  };

  return (
    <div className="ab-config-card">
      <StateBanner config={config} killTriggered={killSwitch?.last_trigger} />

      <div className="ab-config-grid">
        <label className="ab-toggle-row">
          <input
            type="checkbox"
            checked={form.enabled}
            onChange={(e) => setField('enabled', e.target.checked)}
          />
          <span>Auto-block ativo</span>
        </label>

        <label className="ab-toggle-row">
          <input
            type="checkbox"
            checked={form.dry_run}
            onChange={(e) => setField('dry_run', e.target.checked)}
          />
          <span>Modo observação (dry-run)</span>
        </label>

        <label className="ab-toggle-row">
          <input
            type="checkbox"
            checked={form.progressive_enabled}
            onChange={(e) => setField('progressive_enabled', e.target.checked)}
          />
          <span>Bloqueio progressivo</span>
        </label>
      </div>

      <div className="ab-config-grid">
        <div className="ab-config-field">
          <label>maxretry (tentativas até banir)</label>
          <input
            type="number" min="1" max="1000"
            value={form.maxretry}
            onChange={(e) => setField('maxretry', e.target.value)}
          />
        </div>

        <div className="ab-config-field">
          <label>findtime (segundos)</label>
          <input
            type="number" min="10" max="3600"
            value={form.findtime_seconds}
            onChange={(e) => setField('findtime_seconds', e.target.value)}
          />
        </div>

        <div className="ab-config-field">
          <label>Base inicial (segundos)</label>
          <input
            type="number" min="60" max="86400"
            value={form.progressive_base_seconds}
            onChange={(e) => setField('progressive_base_seconds', e.target.value)}
          />
        </div>

        <div className="ab-config-field">
          <label>Cap máximo (segundos)</label>
          <input
            type="number" min="60" max="86400"
            value={form.progressive_max_seconds}
            onChange={(e) => setField('progressive_max_seconds', e.target.value)}
          />
        </div>

        <div className="ab-config-field ab-config-field-wide">
          <label>Níveis progressivos (segundos, crescente — ex: 900, 1800, 3600)</label>
          <input
            type="text"
            value={levelsText}
            onChange={(e) => setLevelsText(e.target.value)}
            placeholder="900, 1800, 3600"
          />
          <div className="ab-help">
            Equivale a: {parseLevels(levelsText).map((s) => {
              const m = Math.floor(s / 60);
              return m >= 60 ? `${Math.floor(m / 60)}h` : `${m}min`;
            }).join(' → ') || '—'}
          </div>
        </div>

        <div className="ab-config-field">
          <label>Kill-switch: threshold de bans</label>
          <input
            type="number" min="1" max="100000"
            value={form.auto_kill_threshold}
            onChange={(e) => setField('auto_kill_threshold', e.target.value)}
          />
        </div>

        <div className="ab-config-field">
          <label>Kill-switch: janela (min)</label>
          <input
            type="number" min="1" max="1440"
            value={form.auto_kill_window_min}
            onChange={(e) => setField('auto_kill_window_min', e.target.value)}
          />
        </div>
      </div>

      {error && <div className="ab-form-error">{error}</div>}

      <div className="ab-config-actions">
        <button
          type="button"
          className="ab-btn-save"
          onClick={handleSave}
          disabled={busy}
        >
          {busy ? 'Salvando...' : 'Salvar configuração'}
        </button>
        <button
          type="button"
          className="ab-btn-kill-switch"
          onClick={onKillSwitch}
          disabled={busy || !config.enabled}
          title="Desativa auto-block imediatamente; IPs já banidos expiram naturalmente"
        >
          🚨 Acionar kill-switch
        </button>
      </div>

      <div className="ab-config-meta">
        <span>Versão: <strong>{config.version}</strong></span>
        <span>Atualizado por: <strong>{config.updated_by || '—'}</strong></span>
        <span>
          Em: <strong>{config.updated_at ? new Date(config.updated_at).toLocaleString('pt-BR') : '—'}</strong>
        </span>
      </div>
    </div>
  );
}

export default AutoBlockConfigCard;
